/**
 * SYSTEM SETTINGS MODULE
 * Per-tenant Incentive Tracker configuration. Reads/writes the account-scoped
 * `incentive_settings` table over RLS — a documented cross-module data contract,
 * exactly like integrationService writes the depletions module's
 * `integration_settings`. We never import the incentive-tracker module
 * (eslint-plugin-boundaries would fail); the table + its RLS
 * (`incentive_is_manager` gates writes to owner/admin) is the contract.
 *
 * The incentive-tracker module reads this same flag independently (its
 * `getRequireReview`) to decide the draft → pending_review vs. draft → sent edge.
 */

import { supabase } from "../../../shared/utils/supabaseClient.js";
import { getMyPrimaryAccount } from "../../../shared/utils/accountService.js";

let _acct = null;
async function acctId() {
  if (_acct) return _acct;
  const a = await getMyPrimaryAccount();
  if (!a?.accountId) throw new Error("No workspace found for your account.");
  _acct = a.accountId;
  return _acct;
}

/** Does this company require manager review before an incentive can be sent? */
export async function getRequireReview() {
  const account = await acctId();
  const { data, error } = await supabase
    .from("incentive_settings")
    .select("require_review")
    .eq("account_id", account)
    .maybeSingle();
  if (error) { console.error("[incentiveSettingsService.getRequireReview]", error); throw new Error("Could not load incentive settings."); }
  return data?.require_review === true;
}

/** Full incentive config: review policy + the Sales Manager (review recipient). */
export async function getIncentiveConfig() {
  const account = await acctId();
  const { data, error } = await supabase
    .from("incentive_settings")
    .select("require_review, sales_manager_user_id")
    .eq("account_id", account)
    .maybeSingle();
  if (error) { console.error("[incentiveSettingsService.getIncentiveConfig]", error); throw new Error("Could not load incentive settings."); }
  return { requireReview: data?.require_review === true, salesManagerUserId: data?.sales_manager_user_id ?? null };
}

/** Set the account-level Sales Manager (the user who reviews incentive programs).
 *  RLS limits this to the workspace owner/admin. Pass "" to clear. */
export async function setSalesManager(userId) {
  const account = await acctId();
  const { error } = await supabase
    .from("incentive_settings")
    .upsert({ account_id: account, sales_manager_user_id: userId || null }, { onConflict: "account_id" });
  if (error) {
    console.error("[incentiveSettingsService.setSalesManager]", error);
    if (error.code === "42501" || /row-level security/i.test(error.message ?? "")) {
      throw new Error("Only workspace owners and admins can change incentive settings.");
    }
    throw new Error("Could not save the Sales Manager.");
  }
}

/** Upsert the review policy. RLS limits this to the workspace owner/admin. */
export async function setRequireReview(value) {
  const account = await acctId();
  const { error } = await supabase
    .from("incentive_settings")
    .upsert({ account_id: account, require_review: !!value }, { onConflict: "account_id" });
  if (error) {
    console.error("[incentiveSettingsService.setRequireReview]", error);
    if (error.code === "42501" || /row-level security/i.test(error.message ?? "")) {
      throw new Error("Only workspace owners and admins can change incentive settings.");
    }
    throw new Error("Could not save the incentive setting.");
  }
}
